import { useSite } from "@inrange/building-manager-api-client";
import {
  SdmMatchConfig,
  Site,
} from "@inrange/building-manager-api-client/models-site";
import { useState } from "react";
import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { DateRangePicker } from "../../../components";
import { useReactQueryError } from "../../../utils/react-query-error";
import CollapsibleCard from "../CollapsibleCard";

const SiteReports = ({ site }: { site: Site }) => {
  const [forecastYear, setForecastYear] = useState<string>("1");
  const [forecastAggregate, setForecastAggregate] = useState<string>("hh");
  const [actualsEFAggregate, setActualsEFAggregate] = useState<string>("hh");
  const [actualsEFStartMonth, setActualsEFStartMonth] = useState<string>("");
  const [actualsEFEndMonth, setActualsEFEndMonth] = useState<string>("");
  const [actualsReadingsAggregate, setActualsReadingsAggregate] =
    useState<string>("hh");
  const [actualsReadingsStartMonth, setActualsReadingsStartMonth] =
    useState<string>("");
  const [actualsReadingsEndMonth, setActualsReadingsEndMonth] =
    useState<string>("");
  const [cashflowType, setCashflowType] = useState<string>("landlord-lease");
  const [cashflowAggregate, setCashflowAggregate] = useState<string>("annual");
  const [selectedSdmMatch, setSelectedSdmMatch] = useState<string>("");
  const [sdmAggregate, setSdmAggregate] = useState<string>("hh");
  const [flexidaoStartDate, setFlexidaoStartDate] = useState<string>("");
  const [flexidaoEndDate, setFlexidaoEndDate] = useState<string>("");
  const [flexidaoError, setFlexidaoError] = useState(false);
  const [loadingReport, setLoadingReport] = useState<string | null>(null);
  const [actualsEFError, setActualsEFError] = useState(false);
  const [actualsReadingsError, setActualsReadingsError] = useState(false);

  const { fetchSiteReport } = useSite({
    siteId: site.id,
    app: "admin",
  });

  useReactQueryError(fetchSiteReport, "Error downloading report");

  const hasAppliedMatches = (site.sdmMatches?.length ?? 0) > 0;
  const showFlexidao =
    site.totalInstalledCapacity === 0 &&
    site.operationalStatus === "operational" &&
    (site.sdmMatches ?? []).some((match) => match.buyerId === site.id);

  const downloadReport = (
    key: string,
    params: Parameters<typeof fetchSiteReport.mutate>[0]
  ) => {
    setLoadingReport(key);
    fetchSiteReport.mutate(params, {
      onSettled: () => setLoadingReport(null),
    });
  };

  const formatMonth = (date: Date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

  const formatDate = (date: Date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
      date.getDate()
    ).padStart(2, "0")}`;

  // Parse a "YYYY-MM-DD" string into a *local* Date so it round-trips with
  // formatDate's local getters and displays the same day the operator picked
  // (new Date("YYYY-MM-DD") would parse as UTC and can drift a day).
  const parseLocalDate = (value: string) => {
    const [year, month, day] = value.split("-").map(Number);
    return new Date(year, month - 1, day);
  };

  return (
    <CollapsibleCard
      title="Reports"
      initialOpen={true}
      className="mt-3 mb-3"
      isBulkEdit={true}
    >
      <fieldset disabled={site.locked}>
        {/* Forecasts */}
        <h6 className="fw-bold">Forecasts</h6>
        <Row className="mt-2 gy-2 align-items-md-end">
          <Col md="2">
            <Form.Group controlId="forecastAggregate">
              <Form.Label>Aggregate</Form.Label>
              <Form.Select
                value={forecastAggregate}
                onChange={(e) => setForecastAggregate(e.target.value)}
              >
                <option value="hh">Half hour</option>
                <option value="day">Day</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md="2">
            <Form.Group controlId="forecastYear">
              <Form.Label>Forecast year</Form.Label>
              <Form.Control
                type="text"
                value={forecastYear}
                onChange={(e) => setForecastYear(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md="2" className="d-grid">
            <Button
              disabled={loadingReport !== null}
              onClick={() =>
                downloadReport("forecast-ef", {
                  reportName: "energy_flows",
                  fileName: "energy-flows",
                  siteName: site.name,
                  options: {
                    groupByDay: forecastAggregate === "day",
                    actuals: false,
                    year: forecastYear,
                  },
                })
              }
            >
              {loadingReport === "forecast-ef" && (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-2"
                />
              )}
              Download energy flow
            </Button>
          </Col>
        </Row>

        <hr className="mt-4" />

        {/* Actuals */}
        <h6 className="fw-bold">Actuals</h6>
        <Row
          className={`mt-2 gy-2 align-items-md-end${actualsEFError ? " mb-md-3" : ""}`}
        >
          <Col md="2">
            <Form.Group controlId="actualsEFAggregate">
              <Form.Label>Aggregate</Form.Label>
              <Form.Select
                value={actualsEFAggregate}
                onChange={(e) => setActualsEFAggregate(e.target.value)}
              >
                <option value="hh">Half hour</option>
                <option value="day">Day</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md="auto">
            <Form.Group controlId="actualsEFRange">
              <Form.Label>Actuals range</Form.Label>
              <DateRangePicker
                value={{
                  start: actualsEFStartMonth
                    ? new Date(`${actualsEFStartMonth}-01`)
                    : null,
                  end: actualsEFEndMonth
                    ? new Date(`${actualsEFEndMonth}-01`)
                    : null,
                }}
                onChange={({ start, end }) => {
                  if (start && end) {
                    setActualsEFStartMonth(formatMonth(start));
                    setActualsEFEndMonth(formatMonth(end));
                    setActualsEFError(false);
                  }
                }}
                mode="month"
                startPlaceholder="Start month"
                endPlaceholder="End month"
              />
              {actualsEFError && (
                <>
                  <div
                    className="d-none d-md-block"
                    style={{ height: 0, overflow: "visible" }}
                  >
                    <div className="text-danger small">
                      Please select a date range
                    </div>
                  </div>
                  <div className="d-md-none text-danger small">
                    Please select a date range
                  </div>
                </>
              )}
            </Form.Group>
          </Col>
          <Col md="2" className="d-grid">
            <Button
              disabled={loadingReport !== null}
              onClick={() => {
                if (!actualsEFStartMonth || !actualsEFEndMonth) {
                  setActualsEFError(true);
                  return;
                }
                downloadReport("actuals-ef", {
                  reportName: "energy_flows",
                  fileName: "energy-flows",
                  siteName: site.name,
                  options: {
                    groupByDay: actualsEFAggregate === "day",
                    actuals: true,
                    startMonth: actualsEFStartMonth,
                    endMonth: actualsEFEndMonth,
                  },
                });
              }}
            >
              {loadingReport === "actuals-ef" && (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-2"
                />
              )}
              Download energy flow
            </Button>
          </Col>
        </Row>
        <Row
          className={`mt-3 gy-2 align-items-md-end${actualsReadingsError ? " mb-md-3" : ""}`}
        >
          <Col md="2">
            <Form.Group controlId="actualsReadingsAggregate">
              <Form.Label>Aggregate</Form.Label>
              <Form.Select
                value={actualsReadingsAggregate}
                onChange={(e) => setActualsReadingsAggregate(e.target.value)}
              >
                <option value="hh">Half hour</option>
                <option value="day">Day</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md="auto">
            <Form.Group controlId="actualsReadingsRange">
              <Form.Label>Actuals range</Form.Label>
              <DateRangePicker
                value={{
                  start: actualsReadingsStartMonth
                    ? new Date(`${actualsReadingsStartMonth}-01`)
                    : null,
                  end: actualsReadingsEndMonth
                    ? new Date(`${actualsReadingsEndMonth}-01`)
                    : null,
                }}
                onChange={({ start, end }) => {
                  if (start && end) {
                    setActualsReadingsStartMonth(formatMonth(start));
                    setActualsReadingsEndMonth(formatMonth(end));
                    setActualsReadingsError(false);
                  }
                }}
                mode="month"
                startPlaceholder="Start month"
                endPlaceholder="End month"
              />
              {actualsReadingsError && (
                <>
                  <div
                    className="d-none d-md-block"
                    style={{ height: 0, overflow: "visible" }}
                  >
                    <div className="text-danger small">
                      Please select a date range
                    </div>
                  </div>
                  <div className="d-md-none text-danger small">
                    Please select a date range
                  </div>
                </>
              )}
            </Form.Group>
          </Col>
          <Col md="2" className="d-grid">
            <Button
              disabled={loadingReport !== null}
              onClick={() => {
                if (!actualsReadingsStartMonth || !actualsReadingsEndMonth) {
                  setActualsReadingsError(true);
                  return;
                }
                downloadReport("actuals-readings", {
                  reportName: "energy_readings_actuals",
                  fileName: "readings-actuals",
                  siteName: site.name,
                  options: {
                    groupByDay: actualsReadingsAggregate === "day",
                    startMonth: actualsReadingsStartMonth,
                    endMonth: actualsReadingsEndMonth,
                  },
                });
              }}
            >
              {loadingReport === "actuals-readings" && (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-2"
                />
              )}
              Download readings
            </Button>
          </Col>
        </Row>

        <hr className="mt-4" />

        {/* Cashflows */}
        <h6 className="fw-bold">Cashflows</h6>
        <Row className="mt-2 gy-2 align-items-md-end">
          <Col md="2">
            <Form.Group controlId="cashflowType">
              <Form.Label>Type</Form.Label>
              <Form.Select
                value={cashflowType}
                onChange={(e) => setCashflowType(e.target.value)}
              >
                <option value="landlord-lease">Landlord, Lease</option>
                <option value="landlord-license">Landlord, License</option>
                <option value="ownerOccupier-lease">OO, Lease</option>
                <option value="ownerOccupier-license">OO, License</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md="2">
            <Form.Group controlId="cashflowAggregate">
              <Form.Label>Aggregate</Form.Label>
              <Form.Select
                value={cashflowAggregate}
                onChange={(e) => setCashflowAggregate(e.target.value)}
              >
                <option value="annual">Annual</option>
                <option value="monthly">Monthly</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md="3" className="d-grid">
            <Button
              disabled={loadingReport !== null}
              onClick={() =>
                downloadReport("customer-cashflow", {
                  siteName: site.name,
                  reportName: "customer_cashflow",
                  fileName: "cashflow",
                  options: {
                    cashflowType,
                    periodType: cashflowAggregate,
                  },
                })
              }
            >
              {loadingReport === "customer-cashflow" && (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-2"
                />
              )}
              Download customer cashflow
            </Button>
          </Col>
        </Row>
        <Row className="mt-3 gy-2">
          <Col md="3" className="d-grid">
            <Button
              disabled={loadingReport !== null}
              onClick={() =>
                downloadReport("spv-cashflow", {
                  siteName: site.name,
                  reportName: "inrange_spv",
                  fileName: "spv-cashflow",
                })
              }
            >
              {loadingReport === "spv-cashflow" && (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-2"
                />
              )}
              Download InRange SPV cashflow
            </Button>
          </Col>
        </Row>

        {/* SDM Match - only show if there's at least one applied match */}
        {hasAppliedMatches && (
          <>
            <hr className="mt-4" />
            <h6 className="fw-bold">SDM Match</h6>
            <Row className="mt-2 gy-2 align-items-md-end">
              <Col md={4}>
                <Form.Group controlId="sdmMatchSelect">
                  <Form.Label>SDM match</Form.Label>
                  <Form.Select
                    value={selectedSdmMatch}
                    onChange={(e) => setSelectedSdmMatch(e.target.value)}
                  >
                    <option value="">Select an SDM match...</option>
                    {site?.sdmMatches?.map((match, index) => (
                      <option key={index} value={JSON.stringify(match)}>
                        {match.sellerName || match.sellerId} →{" "}
                        {match.buyerName || match.buyerId}(
                        {Number(match.volume).toFixed(0)} kWh @{" "}
                        {(Number(match.tariff) * 100).toFixed(2)}p)
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md="2">
                <Form.Group controlId="sdmAggregate">
                  <Form.Label>Aggregate</Form.Label>
                  <Form.Select
                    value={sdmAggregate}
                    onChange={(e) => setSdmAggregate(e.target.value)}
                  >
                    <option value="hh">Half hour</option>
                    <option value="day">Day</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md="3" className="d-grid">
                <Button
                  disabled={!selectedSdmMatch || loadingReport !== null}
                  onClick={() => {
                    const match = JSON.parse(
                      selectedSdmMatch
                    ) as SdmMatchConfig;
                    downloadReport("sdm-ef", {
                      reportName: "sdm_energy_flow",
                      fileName: "sdm-energy-flow",
                      siteName: site.name,
                      options: {
                        buyerSiteId: match.buyerId,
                        buyerSiteEsysUuid: match.buyerEsysUuid,
                        sellerSiteId: match.sellerId,
                        sellerSiteEsysUuid: match.sellerEsysUuid,
                        groupByDay: sdmAggregate === "day",
                      },
                    });
                  }}
                >
                  {loadingReport === "sdm-ef" && (
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      className="me-2"
                    />
                  )}
                  Download SDM energy flow
                </Button>
              </Col>
            </Row>
          </>
        )}

        {showFlexidao && (
          <>
            <hr className="mt-4" />
            <h6 className="fw-bold">Flexidao</h6>
            <Row
              className={`mt-2 gy-2 align-items-md-end${flexidaoError ? " mb-md-3" : ""}`}
            >
              <Col md="auto">
                <Form.Group controlId="flexidaoRange">
                  <Form.Label>Actuals range</Form.Label>
                  <DateRangePicker
                    value={{
                      start: flexidaoStartDate
                        ? parseLocalDate(flexidaoStartDate)
                        : null,
                      end: flexidaoEndDate
                        ? parseLocalDate(flexidaoEndDate)
                        : null,
                    }}
                    onChange={({ start, end }) => {
                      if (start && end) {
                        setFlexidaoStartDate(formatDate(start));
                        setFlexidaoEndDate(formatDate(end));
                        setFlexidaoError(false);
                      }
                    }}
                    mode="date"
                    startPlaceholder="Start date"
                    endPlaceholder="End date"
                  />
                  {flexidaoError && (
                    <>
                      <div
                        className="d-none d-md-block"
                        style={{ height: 0, overflow: "visible" }}
                      >
                        <div className="text-danger small">
                          Please select a date range
                        </div>
                      </div>
                      <div className="d-md-none text-danger small">
                        Please select a date range
                      </div>
                    </>
                  )}
                </Form.Group>
              </Col>
              <Col md="3" className="d-grid">
                <Button
                  disabled={loadingReport !== null}
                  onClick={() => {
                    if (!flexidaoStartDate || !flexidaoEndDate) {
                      setFlexidaoError(true);
                      return;
                    }
                    downloadReport("flexidao", {
                      reportName: "flexidao",
                      fileName: "flexidao-readings",
                      siteName: site.name,
                      options: {
                        startDate: flexidaoStartDate,
                        endDate: flexidaoEndDate,
                      },
                    });
                  }}
                >
                  {loadingReport === "flexidao" && (
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      className="me-2"
                    />
                  )}
                  Download Flexidao readings
                </Button>
              </Col>
            </Row>
          </>
        )}
      </fieldset>
    </CollapsibleCard>
  );
};

export default SiteReports;
